import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuthStore } from '@store/authStore';
import { usePlanFeatures } from '@api/hooks/useSubscription';
import { Navbar } from './Navbar';
import { Sidebar } from './Sidebar';
import { BillingGate } from '@components/billing/BillingGate';
import { ToastContainer } from '@components/common/Toast';
import { ThemeProvider } from '@components/Theme/ThemeProvider';
import { ModalHost } from '@components/Modal/ModalHost';
import { APPostingWorkflowNotifications } from '@components/APPosting/APPostingWorkflowNotifications';
import { DocumentWorkflowNotifications } from '@components/Upload/DocumentWorkflowNotifications';

const VENDOR_ALLOWED_PATHS = ['/vendor-profile', '/vendor-customers', '/vendor-invoices', '/profile'];

export const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, currentUser } = useAuthStore();
  const planFeatures = usePlanFeatures();
  const isVendorTier = planFeatures.vendor_profile === true;

  // Only monitor and redirect if moving to a protected route without auth
  // This prevents aggressive redirects during navigation within the app
  useEffect(() => {
    // Don't check on auth or onboarding pages
    if (location.pathname === '/auth' || location.pathname === '/onboarding') {
      return;
    }

    // If auth is lost while on a protected page, redirect to login
    // But only redirect once per session loss to avoid redirect loops
    if (!isAuthenticated) {
      console.log('[Layout] Auth lost on protected route, redirecting to login...');
      navigate('/auth', { replace: true });
      return;
    }

    // If user hasn't completed onboarding, redirect to onboarding flow
    if (currentUser?.onboarding_complete === false) {
      navigate('/onboarding', { replace: true });
      return;
    }

    // Vendor-tier users can only access vendor-specific routes
    if (isVendorTier && !VENDOR_ALLOWED_PATHS.includes(location.pathname)) {
      navigate('/vendor-profile', { replace: true });
    }
  }, [isAuthenticated, currentUser, navigate, location.pathname, isVendorTier]);

  return (
    <div data-testid="app-shell" className="flex h-screen bg-surface-page">
      <ThemeProvider />
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 flex flex-col overflow-hidden lg:ml-64">
        <Navbar onMenuToggle={() => setSidebarOpen(!sidebarOpen)} />
        <main className="flex-1 overflow-auto">
          <BillingGate>
            <div className="p-4 md:p-8">
              <Outlet />
            </div>
          </BillingGate>
        </main>
      </div>
      <ToastContainer />
      <DocumentWorkflowNotifications />
      <APPostingWorkflowNotifications />
      <ModalHost />
    </div>
  );
};
